import logger from "../../utils/logger";
import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import axiosRetry from "axios-retry";
import config from "../../config";

interface Common {
  apiDomain?: string;
  fetch(options: AxiosRequestConfig): Promise<any>;
}

class CommonService implements Common {
  private axiosInstance: AxiosInstance;
  apiDomain?: string;

  constructor() {
    this.apiDomain = config.links.baseAPIURL;

    this.axiosInstance = axios.create({
      baseURL: this.apiDomain,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    // Add a response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        logger.error("error.response:", error.response, error);
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (error.response.status < 200 || error.response.status >= 400) {
            const errMsg = "Internal server error. Please contact support.";
            logger.info(errMsg);
            return Promise.reject(
              error.response ? error.response : { error: errMsg }
            );
          }
        } else if (error.request) {
          // The request was made but no response was received
          const errMsg = "No response received from server. Please try again.";
          logger.info(errMsg);
          return Promise.reject(
            error.response ? error.response : { error: errMsg }
          );
        } else {
          // Something happened in setting up the request that triggered an Error
          const errMsg = "An error occurred while setting up the request.";
          logger.info(errMsg);
          return Promise.reject(
            error.response ? error.response : { error: errMsg }
          );
        }
        return Promise.reject(error);
      }
    );

    axiosRetry(this.axiosInstance, {
      retries: 3,
      retryDelay: axiosRetry.exponentialDelay,
      retryCondition: (error) => {
        return (
          axiosRetry.isNetworkOrIdempotentRequestError(error) ||
          error.response?.status === 429
        );
      },
    });

    this.fetch = this.fetch.bind(this);
    this.healthCheck = this.healthCheck.bind(this);
  }

  async fetch(options: AxiosRequestConfig) {
    // Check if we need to handle file download responses (like export)
    const isDownloadRequest =
      options.responseType === "blob" || options.url?.includes("/export");

    try {
      // For download requests, we need to set the responseType to 'blob'
      // to get a proper Blob object
      if (isDownloadRequest && !options.responseType) {
        options.responseType = "blob";
      }

      const response = await this.axiosInstance({
        ...options,
      });

      // If this is a download request, create a Response object that has
      // the same interface as the fetch API Response
      if (isDownloadRequest) {
        // Check to see if the "content-type" is an JSON object
        if (
          response.headers["Content-Type"] &&
          typeof response.headers["Content-Type"] === "string" &&
          response.headers["Content-Type"].indexOf("application/json") !== -1
        ) {
          return response.data;
        }

        // Create a blob from the response data
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });

        // Create a Response object with the same API as fetch
        const fetchResponse = new Response(blob, {
          status: response.status,
          statusText: response.statusText,
          headers: new Headers(response.headers as any),
        });

        // Add a shortcut to the blob for convenience
        Object.defineProperty(fetchResponse, "_blob", {
          value: blob,
          writable: false,
        });

        return fetchResponse;
      }

      // For normal API requests (JSON, etc), return just the data
      return response.data;
    } catch (error) {
      // Handle errors as before
      logger.error("Fetch error:", error);
      return Promise.reject(error);
    }
  }

  async healthCheck() {
    try {
      const response = await this.axiosInstance.get("/healthz");
      return response.status;
    } catch (error) {
      return error;
    }
  }
}

export default CommonService;
